import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaGem, FaStar, FaTelegram } from "react-icons/fa";
import { Clock, CheckCircle, Share2, XCircle, RefreshCw, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import WebApp from "@twa-dev/sdk";
import haptic from "../utils/haptic";
import { safeOpenTelegramLink } from "../utils/linkHelpers";
import { API_URL } from "../config";

// ─── Countdown hook ───────────────────────────────────────────────────────────
function useCountdown(active) {
  const [seconds, setSeconds] = useState(3600);
  useEffect(() => {
    if (!active) { setSeconds(3600); return; }
    const id = setInterval(() => setSeconds((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [active]);
  const fmt = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, "0");
    const sec = (s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };
  return { seconds, fmt };
}

// ─── Success state ────────────────────────────────────────────────────────────
function SuccessState({ channel, generatedLink, onClose }) {
  const navigate = useNavigate();
  const { seconds, fmt } = useCountdown(true);
  const isExpired = seconds === 0;

  return (
    <motion.div
      key="success"
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="flex flex-col items-center text-center py-6 gap-5 px-2"
    >
      {/* Animated check */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.1 }}
        className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center"
      >
        <CheckCircle className="text-green-500 dark:text-green-400 w-10 h-10" />
      </motion.div>

      <div>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-1">
          You're in! 🎉
        </h2>
        <p className="text-slate-500 dark:text-slate-400 text-sm">
          You now have access to{" "}
          <span className="font-semibold text-slate-700 dark:text-slate-200">
            {channel.name}
          </span>
        </p>
      </div>

      {/* Countdown */}
      <div className={`flex items-center gap-2 rounded-xl px-4 py-2.5 w-full justify-center border ${
        isExpired
          ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-500/20"
          : "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-500/20"
      }`}>
        <Clock className={`h-4 w-4 shrink-0 ${isExpired ? "text-red-500" : "text-amber-500"}`} />
        <span className="text-sm text-slate-600 dark:text-slate-300">
          {isExpired ? (
            <span className="font-semibold text-red-500 dark:text-red-400">Link expired</span>
          ) : (
            <>
              Link expires in{" "}
              <span className="font-bold text-amber-600 dark:text-amber-300 tabular-nums">
                {fmt(seconds)}
              </span>
            </>
          )}
        </span>
      </div>

      {/* Primary CTA */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        disabled={isExpired}
        onClick={() => {
          haptic.tap();
          safeOpenTelegramLink(generatedLink);
        }}
        className={`w-full py-3.5 rounded-2xl font-bold text-base flex items-center justify-center gap-2 transition-all ${
          isExpired
            ? "bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed"
            : "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25"
        }`}
      >
        <FaTelegram size={18} />
        {isExpired ? "Link Expired" : "Join Channel Now"}
      </motion.button>

      {/* Secondary */}
      <button
        onClick={() => { haptic.tap(); onClose(); navigate("/purchased-movies"); }}
        className="text-sm text-slate-500 dark:text-slate-400 underline underline-offset-2 hover:text-purple-500 transition-colors"
      >
        View My Library
      </button>
    </motion.div>
  );
}

// ─── Detail / buy state ───────────────────────────────────────────────────────
function DetailContent({
  channel,
  userId,
  userPoints,
  existingLinks,
  onGemsPurchase,
  onStarsPurchase,
  onTonPurchase,
  purchaseState,
  buttonLoading,
  onClose,
}) {
  const navigate = useNavigate();
  const canAffordGems = channel.points > 0 && userPoints >= channel.points;
  const linkCount = existingLinks?.filter((l) => l.name === channel.name).length || 0;
  const linkLimitReached = linkCount >= 3;
  const isLoading = buttonLoading || purchaseState === "loading";

  const hasConditions = channel.joinConditions?.length > 0;

  // ── Condition-checking state ──────────────────────────────────────────────
  const [conditionResults, setConditionResults] = useState(null); // null = not checked yet
  const [conditionsLoading, setConditionsLoading] = useState(false);
  const [manualAcks, setManualAcks] = useState({}); // idx -> bool for custom_text
  const [savingAcknowledgement, setSavingAcknowledgement] = useState(false);
  const [requirementsError, setRequirementsError] = useState("");

  const applyRequirementResults = (data) => {
    const results = data.results || [];
    setConditionResults(results);
    setManualAcks(Object.fromEntries(results.map((result, i) => [i, result.manual && result.met === true])));
  };

  const saveAcknowledgement = async (index, checked) => {
    setSavingAcknowledgement(true);
    setRequirementsError("");
    const selected = { ...manualAcks, [index]: checked };
    try {
      const res = await fetch(
        `${API_URL}/api/channels/${channel._id}/acknowledge-requirements/${userId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-api-key": process.env.REACT_APP_API_KEY || "" },
          body: JSON.stringify({
            initData: WebApp.initData,
            conditionIds: channel.joinConditions.filter((condition, i) => condition.type === "custom_text" && selected[i]).map(condition => condition._id),
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Unable to save acknowledgement");
      applyRequirementResults(data);
    } catch (error) {
      setRequirementsError(error.message || "Unable to save acknowledgement. Please try again.");
    } finally {
      setSavingAcknowledgement(false);
    }
  };

  const checkConditions = async () => {
    if (!hasConditions || !userId) return;
    setConditionsLoading(true);
    setRequirementsError("");
    try {
      const res = await fetch(
        `${API_URL}/api/channels/${channel._id}/check-conditions/${userId}?initData=${encodeURIComponent(WebApp.initData)}`,
        { headers: { "x-api-key": process.env.REACT_APP_API_KEY || "" } }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Unable to check requirements");
      applyRequirementResults(data);
    } catch (error) {
      setConditionResults([]);
      setRequirementsError(error.message || "Unable to check requirements. Please try again.");
    } finally {
      setConditionsLoading(false);
    }
  };

  useEffect(() => {
    if (hasConditions) checkConditions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel._id]);

  // All conditions pass when:
  // - no conditions exist, OR
  // - every auto-checked condition passes AND every custom_text is manually acknowledged
  const conditionsMet = !hasConditions || (
    !conditionsLoading && !savingAcknowledgement && conditionResults !== null &&
    conditionResults.length === channel.joinConditions.length &&
    conditionResults.every((r, i) =>
      r.manual ? (manualAcks[i] === true) : r.met
    )
  );

  const CONDITION_LABEL = {
    min_gems: (val) => `Own at least ${Number(val).toLocaleString()} gems`,
    min_referrals: (val) => `Have at least ${val} referral${val !== "1" ? "s" : ""}`,
    telegram_member: (val) => `Be a member of ${val}`,
    custom_text: (val) => val,
  };

  // ── Watch Ads to Unlock state ───────────────────────────────────────────────
  const adsRequired = channel.adsToUnlock || 0;
  const hasAdUnlock = adsRequired > 0;
  const adsMakeFree = channel.adsMakeFree !== false;
  const [adWatchCount, setAdWatchCount] = useState(0);   // synced from server
  const [adWatching, setAdWatching] = useState(false);   // ad in progress
  const isAdsLocked = hasAdUnlock && !adsMakeFree && adWatchCount < adsRequired;

  // Load existing ad progress from server on mount.
  useEffect(() => {
    if (!hasAdUnlock || !userId) return;

    // Fetch current progress
    fetch(`${API_URL}/api/channels/${channel._id}/ad-progress/${userId}?initData=${encodeURIComponent(WebApp.initData)}`, {
      headers: { "x-api-key": process.env.REACT_APP_API_KEY || "" },
    })
      .then((r) => r.json())
      .then((d) => { if (typeof d.count === "number") setAdWatchCount(d.count); })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel._id]);

  const handleWatchAd = async () => {
    if (adWatching || adWatchCount >= adsRequired) return;
    setAdWatching(true);
    haptic.tap?.();
    try {
      // Show the Monetag ad
      let adOk = false;
      if (typeof window.show_8843260 === "function") {
        adOk = await window.show_8843260().then(() => true).catch(() => false);
      } else {
        // Dev / no-SDK fallback
        adOk = true;
      }

      if (!adOk) {
        setAdWatching(false);
        return;
      }

      // Record watch on server
      const res = await fetch(
        `${API_URL}/api/channels/${channel._id}/record-ad-watch/${userId}?initData=${encodeURIComponent(WebApp.initData)}`,
        { method: "POST", headers: { "x-api-key": process.env.REACT_APP_API_KEY || "" } }
      );
      const data = await res.json();

      if (!res.ok) {
        console.warn("Ad record error:", data.error);
        setAdWatching(false);
        return;
      }

      setAdWatchCount(data.count);

      if (data.unlocked) {
        haptic.notification?.("success");
      }
    } catch (err) {
      console.error("Watch ad error:", err);
    } finally {
      setAdWatching(false);
    }
  };

  const isFree =
    (!channel.points || channel.points === 0) &&
    (!channel.stars || channel.stars === 0) &&
    (!channel.price || channel.price === 0) &&
    (!channel.tonStars || channel.tonStars === 0);

  return (
    <motion.div
      key="detail"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      {/* Thumbnail */}
      {channel.thumbnail && (
        <div className="relative w-full h-44 rounded-2xl overflow-hidden mb-4 mt-1">
          <img
            src={channel.thumbnail}
            alt={channel.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          {channel.isSuperFeatured ? (
            <span className="absolute top-3 right-3 bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-md">
              Popular
            </span>
          ) : channel.isFeatured ? (
            <span className="absolute top-3 right-3 bg-sky-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
              Featured
            </span>
          ) : null}
        </div>
      )}

      {/* Title row */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white leading-tight">
            {channel.name}
          </h2>
          {!channel.thumbnail && channel.isSuperFeatured ? (
            <span className="inline-flex mt-1 items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white shadow-md">
              ★ Popular
            </span>
          ) : !channel.thumbnail && channel.isFeatured ? (
            <span className="inline-flex mt-1 items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-300">
              ★ Featured
            </span>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ${
            channel.status === "active"
              ? "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400"
              : channel.status === "paid"
              ? "bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400"
              : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400"
          }`}>
            {channel.status === "active" ? "Available" : channel.status === "closed" ? "Closed" : channel.status === "paid" ? "Premium" : "Coming Soon"}
          </span>
          <button
            onClick={() => {
              haptic.tap();
              const shareLink = `https://t.me/biscopemyanmar_bot/store?startapp=channel_${channel._id}`;
              safeOpenTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(shareLink)}&text=${encodeURIComponent("Check out " + channel.name + " on Biscope Store!")}`);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 transition-colors"
          >
            <Share2 size={14} />
            <span className="text-xs font-semibold">Share</span>
          </button>
        </div>
      </div>

      {/* Description */}
      {channel.description && (
        <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed mb-4">
          {channel.description}
        </p>
      )}

      {/* Closing date */}
      {channel.closedDate && channel.status === "active" && (
        <div className="flex items-center gap-2 mb-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-xl px-3 py-2">
          <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse shrink-0" />
          <p className="text-amber-700 dark:text-amber-300 text-xs">
            Available until {new Date(channel.closedDate).toLocaleDateString("en-GB")}
          </p>
        </div>
      )}

      {/* Cast */}
      {channel.casting?.length > 0 && (
        <div className="mb-5 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700 p-3">
          <p className="text-slate-700 dark:text-slate-200 text-sm flex gap-2 items-start">
            <span className="text-base shrink-0">🎭</span>
            <span>
              {channel.casting.map((c, i) => (
                <span key={i} className="text-slate-800 dark:text-slate-200">
                  {c.name}{c.role ? ` (${c.role})` : ""}
                  {i < channel.casting.length - 1 ? " · " : ""}
                </span>
              ))}
            </span>
          </p>
        </div>
      )}

      {/* ── Join Conditions ── */}
      {hasConditions && (
        <div className="mb-5 rounded-xl border border-slate-200 dark:border-slate-700/60 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700/60">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Requirements</p>
            {!conditionsMet && (
              <button
                onClick={() => { haptic.tap(); checkConditions(); }}
                disabled={conditionsLoading || savingAcknowledgement}
                className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors disabled:opacity-40"
                title="Re-check conditions"
              >
                <RefreshCw size={13} className={conditionsLoading ? "animate-spin" : ""} />
                Check
              </button>
            )}
          </div>

          {/* Condition rows */}
          <div className="divide-y divide-slate-100 dark:divide-slate-700/40">
            {channel.joinConditions.map((cond, i) => {
              const result = conditionResults?.[i];
              const isManual = cond.type === "custom_text";
              const met = isManual ? (manualAcks[i] === true) : result?.met;
              const checking = conditionsLoading || result === undefined;
              const label = cond.label || CONDITION_LABEL[cond.type]?.(cond.value) || cond.value;

              return (
                <div key={i} className="flex items-center gap-3 px-3 py-2.5">
                  {/* Status icon */}
                  <div className="shrink-0">
                    {checking ? (
                      <span className="w-4 h-4 border-2 border-slate-300 dark:border-slate-600 border-t-slate-500 rounded-full animate-spin inline-block" />
                    ) : met ? (
                      <CheckCircle size={16} className="text-green-500" />
                    ) : (
                      <XCircle size={16} className="text-red-400" />
                    )}
                  </div>

                  {/* Label */}
                  <div className="flex-1 flex flex-col items-start gap-1.5 py-0.5">
                    <p className={`text-sm ${
                      checking ? "text-slate-400 dark:text-slate-500" :
                      met ? "text-slate-700 dark:text-slate-200" :
                      "text-slate-600 dark:text-slate-400"
                    }`}>
                      {label}
                    </p>
                    
                    {/* Action button if not met */}
                    {!met && !checking && !isManual && (
                      <button
                        onClick={() => {
                          haptic.tap();
                          if (cond.type === "min_gems") {
                            onClose();
                            navigate("/tasks");
                          } else if (cond.type === "min_referrals") {
                            onClose();
                            navigate("/profile");
                          } else if (cond.type === "telegram_member") {
                            let link = cond.value;
                            if (link.startsWith("@")) link = "https://t.me/" + link.substring(1);
                            try { safeOpenTelegramLink(link); } catch(e) { window.open(link, "_blank"); }
                          }
                        }}
                        className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 px-3 py-1 rounded-full font-medium active:scale-95 transition-transform shadow-sm"
                      >
                        {cond.type === "min_gems" ? "Earn Gems" : cond.type === "min_referrals" ? "Invite Friends" : cond.type === "telegram_member" ? "Join Channel" : "Complete"}
                      </button>
                    )}
                  </div>

                  {/* Manual ack checkbox for custom_text */}
                  {isManual && (
                    <input
                      type="checkbox"
                      checked={manualAcks[i] === true}
                      onChange={(e) => saveAcknowledgement(i, e.target.checked)}
                      disabled={savingAcknowledgement || conditionsLoading}
                      className="h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-blue-500 focus:ring-blue-500 cursor-pointer shrink-0"
                    />
                  )}
                </div>
              );
            })}
          </div>

          {requirementsError && (
            <p role="alert" className="px-3 py-2 text-xs text-red-600 dark:text-red-400">{requirementsError}</p>
          )}
          {/* Gate message */}
          {!conditionsMet && conditionResults !== null && (
            <div className="px-3 py-2 bg-red-50 dark:bg-red-900/10 border-t border-red-100 dark:border-red-800/30">
              <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                Complete all requirements to unlock purchase
              </p>
            </div>
          )}
        </div>
      )}

      <div className="border-t border-slate-200 dark:border-slate-700 mb-4" />

      {/* ── Buy section ── */}
      {channel.status === "active" || channel.status === "closed" || channel.status === "paid" ? (
        <div className="flex flex-col gap-2.5">
          <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
            Get Access
          </p>

          {/* Gems / Free */}
          {!isAdsLocked && (channel.points > 0 || (isFree && !hasAdUnlock)) && (
            <>
              <motion.button
                whileTap={{ scale: 0.98 }}
                disabled={isLoading || linkLimitReached || (!isFree && !canAffordGems) || !conditionsMet}
                onClick={() => onGemsPurchase(channel)}
                className={`w-full py-3.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${
                  isLoading
                    ? "bg-purple-400 text-white cursor-wait"
                    : linkLimitReached
                      ? "bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed"
                      : !conditionsMet
                        ? "bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed"
                        : !isFree && !canAffordGems
                          ? "bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed"
                          : isFree
                            ? "bg-gradient-to-r from-[#0C8BC4] to-sky-500 text-white shadow-md shadow-sky-500/20"
                            : "bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white shadow-md shadow-purple-500/20"
                }`}
              >
                {isLoading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Generating link…
                  </>
                ) : linkLimitReached ? (
                  "Link limit reached (3 / 3)"
                ) : !conditionsMet ? (
                  "Requirements not met 🔒"
                ) : isFree ? (
                  "Free — Get Invite Link 🚀"
                ) : !canAffordGems ? (
                  <>
                    <FaGem />
                    Need {channel.points - userPoints} more gems
                  </>
                ) : (
                  <>
                    <FaGem />
                    {channel.points} Gems — Get Invite Link
                    {channel.salePoints > 0 && (
                      <span className="line-through text-sm opacity-60 ml-1">{channel.salePoints}</span>
                    )}
                  </>
                )}
              </motion.button>

              {/* Earn more gems shortcuts */}
              {!isFree && !canAffordGems && !linkLimitReached && (
                <div className="flex gap-2">
                  <button
                    onClick={() => { haptic.tap(); onClose(); navigate("/tasks"); }}
                    className="flex-1 text-xs py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-medium"
                  >
                    Complete Tasks
                  </button>
                  <button
                    onClick={() => { haptic.tap(); onClose(); navigate("/store"); }}
                    className="flex-1 text-xs py-2 rounded-xl bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300 font-medium"
                  >
                    Buy Gems
                  </button>
                </div>
              )}
            </>
          )}

          {/* Stars */}
          {!isAdsLocked && channel.stars > 0 && (
            <motion.button
              whileTap={{ scale: 0.98 }}
              disabled={isLoading || !conditionsMet}
              onClick={() => onStarsPurchase(channel)}
              className="w-full py-3 rounded-2xl font-semibold text-sm border-2 border-amber-400 dark:border-yellow-500 text-amber-600 dark:text-yellow-400 hover:bg-amber-50 dark:hover:bg-yellow-500/10 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaStar className="text-amber-500" />
              {channel.stars} Stars — Buy Now
              {channel.saleStars > 0 && (
                <span className="line-through text-xs opacity-60 ml-1">{channel.saleStars}</span>
              )}
            </motion.button>
          )}

          {/* Kyats */}
          {!isAdsLocked && channel.price > 0 && (
            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={() => { haptic.tap(); safeOpenTelegramLink(channel.url); }}
              className="w-full py-3 rounded-2xl font-semibold text-sm border-2 border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center justify-center gap-2"
            >
              {channel.price.toLocaleString()} Kyats
              {channel.salePrice > 0 && (
                <span className="line-through text-xs opacity-60 ml-1">{channel.salePrice.toLocaleString()}</span>
              )}
            </motion.button>
          )}

          {/* TON */}
          {!isAdsLocked && channel.tonStars > 0 && (
            <motion.button
              whileTap={{ scale: 0.98 }}
              disabled={isLoading || !conditionsMet}
              onClick={() => onTonPurchase(channel)}
              className="w-full py-3 rounded-2xl font-semibold text-sm bg-[#0088cc] hover:bg-[#0077b3] text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="font-bold">{channel.tonStars} TON</span>
              <span className="text-xs opacity-80">Crypto</span>
              {!conditionsMet && <span className="text-xs opacity-70">🔒</span>}
            </motion.button>
          )}

          {/* Watch Ads to Unlock */}
          {hasAdUnlock && (adsMakeFree || adWatchCount < adsRequired) && (
            <div className="flex flex-col gap-1.5 mt-1">
              {/* Separator */}
              {!isFree && (
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                  <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">or watch ads</span>
                  <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                </div>
              )}

              {adWatchCount >= adsRequired ? (
                /* Unlocked — show generate link button */
                <motion.button
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={isLoading || linkLimitReached || !conditionsMet}
                  onClick={() => { haptic.tap?.(); onGemsPurchase(channel); }}
                  className={`w-full py-3 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${
                    isLoading
                      ? "bg-emerald-400 text-white cursor-wait"
                      : linkLimitReached || !conditionsMet
                        ? "bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-md shadow-green-500/20 hover:brightness-110"
                  }`}
                >
                  {isLoading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      Generating link…
                    </>
                  ) : linkLimitReached ? (
                    "Link limit reached (3 / 3)"
                  ) : !conditionsMet ? (
                    "Requirements not met 🔒"
                  ) : (
                    <>
                      <CheckCircle size={16} />
                      Get Invite Link
                    </>
                  )}
                </motion.button>
              ) : (
                <>
                  {/* Progress bar */}
                  <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-1.5 overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-fuchsia-500 to-purple-500 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${(adWatchCount / adsRequired) * 100}%` }}
                      transition={{ duration: 0.4 }}
                    />
                  </div>

                  {/* Watch button */}
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    disabled={adWatching || !conditionsMet || linkLimitReached}
                    onClick={handleWatchAd}
                    className={`w-full py-3 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
                      adWatching
                        ? "bg-fuchsia-500/60 text-white cursor-wait"
                        : linkLimitReached
                          ? "bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed"
                          : !conditionsMet
                            ? "bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed"
                            : "bg-gradient-to-r from-fuchsia-600 to-purple-600 text-white shadow-md shadow-fuchsia-500/20 hover:brightness-110"
                    }`}
                  >
                    {adWatching ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        Loading ad…
                      </>
                    ) : linkLimitReached ? (
                      "Link limit reached (3 / 3)"
                    ) : !conditionsMet ? (
                      "Requirements not met 🔒"
                    ) : linkLimitReached ? (
                      "Link limit reached (3 / 3)"
                    ) : !conditionsMet ? (
                      "Requirements not met 🔒"
                    ) : (
                      <>
                        🎬 Watch Ad {adWatchCount}/{adsRequired} — {adsMakeFree ? "Free Access" : "Unlock Purchase"}
                      </>
                    )}
                  </motion.button>
                </>
              )}
            </div>
          )}

          {/* Error message */}
          {purchaseState === "error" && (
            <motion.p
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2 text-sm text-red-500 dark:text-red-400 text-center"
            >
              Something went wrong.{" "}
              <button
                className="underline font-medium"
                onClick={() => safeOpenTelegramLink("https://t.me/biscopesupport_bot")}
              >
                Contact support
              </button>
            </motion.p>
          )}
        </div>
      ) : (
        <button
          disabled
          className="w-full py-3.5 rounded-2xl font-bold text-sm bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed"
        >
          Coming Soon
        </button>
      )}
    </motion.div>
  );
}

// ─── Main sheet ───────────────────────────────────────────────────────────────
const ChannelDetailSheet = ({
  channel,
  onClose,
  userId,
  userPoints,
  existingLinks,
  onGemsPurchase,
  onStarsPurchase,
  onTonPurchase,
  purchaseState,
  generatedLink,
  buttonLoading,
}) => {
  if (!channel) return null;

  return (
    <>
      {/* Backdrop */}
      <motion.div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />

      {/* Sheet */}
      <motion.div
        className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-slate-900 rounded-t-3xl"
        style={{ maxHeight: "90vh" }}
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", stiffness: 300, damping: 32 }}
        drag="y"
        dragConstraints={{ top: 0 }}
        dragElastic={0.1}
        onDragEnd={(_, info) => { if (info.offset.y > 100) onClose(); }}
      >
        {/* Drag handle + close button */}
        <div className="relative flex justify-center items-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-slate-300 dark:bg-slate-600" />
          <button
            onClick={onClose}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable content */}
        <div
          className="overflow-y-auto px-5 pb-24"
          style={{ maxHeight: "calc(90vh - 32px)" }}
        >
          <AnimatePresence mode="wait">
            {purchaseState === "success" ? (
              <SuccessState
                key="success"
                channel={channel}
                generatedLink={generatedLink}
                onClose={onClose}
              />
            ) : (
              <DetailContent
                key="detail"
                channel={channel}
                userId={userId}
                userPoints={userPoints}
                existingLinks={existingLinks}
                onGemsPurchase={onGemsPurchase}
                onStarsPurchase={onStarsPurchase}
                onTonPurchase={onTonPurchase}
                purchaseState={purchaseState}
                buttonLoading={buttonLoading}
                onClose={onClose}
              />
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </>
  );
};

export default ChannelDetailSheet;
